/**
 * MCP tool: yap_validate_design — the design-completeness gate.
 *
 * Pure reads over the BIM store (no renderer coupling): it reports the gaps
 * between "some elements exist" and "a complete architectural deliverable"
 * (levels, envelope, slab per level, roof, joined corners, live openings,
 * annotations). The agent's finish line is zero blocking gaps here — the
 * design playbook (ortam system prompt) codifies that contract.
 */

import { useBIMStore } from '../../state/useBIMStore';
import type { BIMElement, FloorElement, RoofElement, WallElement } from '../../core/types';
import type { McpToolHandler } from './mcpResultUtils';
import { okJson } from './mcpResultUtils';

type GapSeverity = 'blocking' | 'warning';

interface DesignGap {
    code: string;
    severity: GapSeverity;
    detail: string;
}

interface BBox2D {
    minX: number;
    minY: number;
    maxX: number;
    maxY: number;
}

function bboxOfLoop(loop: [number, number][] | null | undefined): BBox2D | null {
    if (!loop || loop.length === 0) return null;
    const xs = loop.map((p) => p[0]);
    const ys = loop.map((p) => p[1]);
    return { minX: Math.min(...xs), minY: Math.min(...ys), maxX: Math.max(...xs), maxY: Math.max(...ys) };
}

function bboxOverlaps(a: BBox2D, b: BBox2D): boolean {
    return a.minX <= b.maxX && a.maxX >= b.minX && a.minY <= b.maxY && a.maxY >= b.minY;
}

function planLoopOf(element: BIMElement): [number, number][] | null {
    const geom = (element as { geometryData?: Record<string, unknown> }).geometryData;
    if (!geom) return null;
    const loop = geom.boundary ?? geom.footprint;
    if (!Array.isArray(loop) || loop.length < 3) return null;
    return loop.map((p) => {
        const arr = Array.isArray(p) ? p : [];
        return [Number(arr[0]) || 0, Number(arr[1]) || 0] as [number, number];
    });
}

function wallPlanRun(wall: WallElement): [number, number][] | null {
    const g = wall.geometryData;
    if (!Array.isArray(g?.start) || !Array.isArray(g?.end)) return null;
    return [[g.start[0], g.start[1]], [g.end[0], g.end[1]]];
}

export const handleValidateDesign: McpToolHandler = () => {
    const project = useBIMStore.getState().project;
    const elements = Array.from(project.elements.values());
    const walls = elements.filter((e): e is WallElement => e.category === 'Walls');
    const floors = elements.filter((e): e is FloorElement => e.category === 'Floors');
    const roofs = elements.filter((e): e is RoofElement => e.category === 'Roofs');
    const openings = elements.filter((e) => e.category === 'Doors' || e.category === 'Windows');
    const dimensions = elements.filter((e) => e.category === 'Dimensions');
    const roomLabels = elements.filter((e) => e.category === 'Rooms' || e.category === 'Tags' || e.category === 'Text Notes');

    const gaps: DesignGap[] = [];
    const gap = (code: string, severity: GapSeverity, detail: string) => gaps.push({ code, severity, detail });

    // 1. Levels — the datum layer every other check keys off.
    const levels = Array.from(project.levels.values());
    if (levels.length === 0) gap('NO_LEVELS', 'blocking', 'no Levels defined — create one per story via yap_create_element category "Levels"');
    if (walls.length === 0) gap('NO_WALLS', 'blocking', 'no Walls — the envelope has not been started');

    // 2. Slab per level: every level that carries walls must have a Floors
    //    element whose boundary overlaps that level's wall envelope.
    const wallRunsByLevel = new Map<string, [number, number][]>();
    for (const wall of walls) {
        const run = wallPlanRun(wall);
        if (!run) continue;
        const key = wall.geometryData.baseConstraintId || '';
        const existing = wallRunsByLevel.get(key) ?? [];
        wallRunsByLevel.set(key, [...existing, ...run]);
    }
    const floorBoxes = floors
        .map((f) => ({ element: f, box: bboxOfLoop(planLoopOf(f)) }))
        .filter((e): e is { element: FloorElement; box: BBox2D } => e.box !== null);
    const levelName = (id: string) => project.levels.get(id)?.name ?? (id || 'unconstrained');
    for (const [levelId, runs] of wallRunsByLevel) {
        const envelope = bboxOfLoop(runs);
        if (!envelope) continue;
        const covered = floorBoxes.some(
            (f) => (f.element.geometryData.levelId ?? '') === levelId && bboxOverlaps(f.box, envelope),
        );
        const coveredAnywhere = floorBoxes.some((f) => bboxOverlaps(f.box, envelope));
        if (!covered && !coveredAnywhere) {
            gap('NO_FLOOR', 'blocking', `no Floors element covers the wall envelope on level '${levelName(levelId)}'`);
        } else if (!covered && coveredAnywhere) {
            gap('FLOOR_LEVEL_MISMATCH', 'warning', `a Floors element overlaps the walls of level '${levelName(levelId)}' but is not assigned to it (set its levelId)`);
        }
    }

    // 3. Roof — the top must be closed.
    if (walls.length > 0) {
        const wallRuns = walls.flatMap((w) => wallPlanRun(w) ?? []);
        const envelope = bboxOfLoop(wallRuns);
        const roofBoxes = roofs
            .map((r) => bboxOfLoop(planLoopOf(r)))
            .filter((b): b is BBox2D => b !== null);
        if (!envelope || roofBoxes.length === 0 || !roofBoxes.some((b) => bboxOverlaps(b, envelope))) {
            gap('NO_ROOF', 'blocking', 'no Roofs element intersects the wall envelope');
        }
    }

    // 4. Wall corners — every plan endpoint should be shared by 2+ walls;
    //    a floating end is usually a half-drawn partition.
    const endpointUse = new Map<string, number>();
    for (const wall of walls) {
        const g = wall.geometryData;
        if (!Array.isArray(g?.start) || !Array.isArray(g?.end)) continue;
        for (const p of [g.start, g.end] as [number, number, number][]) {
            const key = `${p[0].toFixed(3)},${p[1].toFixed(3)}`;
            endpointUse.set(key, (endpointUse.get(key) ?? 0) + 1);
        }
    }
    const floating = Array.from(endpointUse.entries()).filter(([, n]) => n === 1);
    if (floating.length > 0) {
        gap(
            'OPEN_WALL_ENDS',
            'warning',
            `${floating.length} wall endpoint(s) are not shared by another wall (floating partition ends): ${floating.slice(0, 5).map(([k]) => `(${k})`).join(', ')}${floating.length > 5 ? ' …' : ''}`,
        );
    }

    // 5. Openings must reference live walls.
    const orphanOpenings = openings.filter((o) => {
        const host = (o as unknown as { geometryData?: { hostWallId?: string } }).geometryData?.hostWallId;
        return !host || !project.elements.has(host);
    });
    if (openings.length > 0 && orphanOpenings.length > 0) {
        gap('ORPHAN_OPENINGS', 'blocking', `${orphanOpenings.length} door/window(s) reference walls that no longer exist: ${orphanOpenings.slice(0, 5).map((o) => o.name).join(', ')}`);
    }

    // 6. Annotations — documentation starts with dimensions and labels.
    if (walls.length > 0 && dimensions.length === 0) {
        gap('NO_DIMENSIONS', 'warning', 'no Dimensions on the model — add the exterior dimension chain');
    }
    if (walls.length > 0 && roomLabels.length === 0) {
        gap('NO_ROOM_LABELS', 'warning', 'no room labels/tags — name the rooms');
    }

    const blockingCount = gaps.filter((g) => g.severity === 'blocking').length;
    return okJson(
        {
            ok: true,
            complete: blockingCount === 0,
            blockingCount,
            warningCount: gaps.length - blockingCount,
            gaps,
            counts: {
                levels: levels.length,
                walls: walls.length,
                floors: floors.length,
                roofs: roofs.length,
                doors: openings.filter((o) => o.category === 'Doors').length,
                windows: openings.filter((o) => o.category === 'Windows').length,
                dimensions: dimensions.length,
                roomLabels: roomLabels.length,
            },
        },
        blockingCount === 0
            ? `Design complete: 0 blocking gap(s), ${gaps.length - blockingCount} warning(s).`
            : `Design INCOMPLETE: ${blockingCount} blocking gap(s), ${gaps.length - blockingCount} warning(s). Fix every blocking gap before finishing.`,
    );
};
